import type { CommandPaletteAiCandidate } from './command-palette-ai-contract';
import type { Command } from './types';

import { extractTextFromCommandLabel } from './command-label-text';
import { commandScore } from './use-fuzzy-search';

export type AiCommandCandidate = CommandPaletteAiCandidate;

const DEFAULT_AI_COMMAND_CANDIDATE_LIMIT = 20;
const TOKEN_DELIMITER_REGEXP = /[\s\\/_+.#"@([{&-]+/;
const QUERY_STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'can',
  'do',
  'for',
  'how',
  'i',
  'in',
  'is',
  'me',
  'of',
  'on',
  'open',
  'show',
  'the',
  'to',
  'where',
]);

function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

function getQueryTokens(query: string): string[] {
  return normalizeText(query)
    .split(TOKEN_DELIMITER_REGEXP)
    .map((token) => token.trim())
    .filter((token) => token.length > 1 && !QUERY_STOP_WORDS.has(token));
}

function getSearchableValues(candidate: AiCommandCandidate): string[] {
  return [
    candidate.label,
    candidate.description ?? '',
    ...(candidate.keywords ?? []),
    ...(candidate.examples ?? []),
  ].filter((value) => value.trim().length > 0);
}

function computeTokenOverlapScore(query: string, values: string[]): number {
  const queryTokens = getQueryTokens(query);
  if (queryTokens.length === 0) {
    return 0;
  }

  const searchableText = normalizeText(values.join(' '));
  const matchedTokenCount = queryTokens.filter((token) => searchableText.includes(token)).length;
  return matchedTokenCount / queryTokens.length;
}

function computeAiCandidateScore(candidate: AiCommandCandidate, query: string): number {
  const searchableValues = getSearchableValues(candidate);
  const fuzzyScore = searchableValues.reduce((highestScore, value) => {
    const score = commandScore(value, query);
    return score > highestScore ? score : highestScore;
  }, 0);
  const tokenOverlapScore = computeTokenOverlapScore(query, searchableValues);

  return fuzzyScore + tokenOverlapScore;
}

export function serializeCommandsForAi(commands: Command[]): AiCommandCandidate[] {
  return commands.flatMap((command) => {
    if (command.tag || command.safeExecution !== 'navigation') {
      return [];
    }

    const label = extractTextFromCommandLabel(command.label);
    if (!label) {
      return [];
    }

    return [
      {
        id: command.id,
        label,
        ...(command.group ? { group: command.group } : {}),
        ...(command.keywords?.length ? { keywords: command.keywords } : {}),
        ...(command.description ? { description: command.description } : {}),
        ...(command.examples?.length ? { examples: command.examples } : {}),
        safeExecution: command.safeExecution,
      },
    ];
  });
}

export function getAiCommandCandidates(
  commands: Command[],
  query: string,
  limit = DEFAULT_AI_COMMAND_CANDIDATE_LIMIT,
): AiCommandCandidate[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return [];
  }

  const scoredCandidates = serializeCommandsForAi(commands)
    .map((candidate) => ({
      candidate,
      score: computeAiCandidateScore(candidate, trimmedQuery),
    }))
    .sort((a, b) => b.score - a.score);

  const positiveMatches = scoredCandidates.filter((entry) => entry.score > 0);
  const candidateSource = positiveMatches.length > 0 ? positiveMatches : scoredCandidates;

  return candidateSource.slice(0, limit).map((entry) => entry.candidate);
}
